import { useState } from "react"
import { useParams, Link, useLocation } from "wouter"
import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { MapPin, Clock, Tent, Users, ChevronLeft } from "lucide-react"
import { api } from "@/lib/api"
import { formatCurrency, LOCALE_MAP } from "@/lib/utils"
import { useAdminAuthStore } from "@/store/adminAuthStore"
import { PhotoGallery } from "@/components/PhotoGallery"
import { AIGuidePanel } from "@/components/AIGuidePanel"
import { CollapsibleText } from "@/components/CollapsibleText"
import { OperatingRulesModal } from "@/components/OperatingRulesModal"
import { sanitizeRichText, isHtmlContent } from "@/lib/sanitize"
import type { ArealPublicDetail, ArealResponse } from "@fohet/caravan-booker-shared/types"

export const CampDetailPage = () => {
    const { slug } = useParams<{ slug: string }>()
    const { t, i18n } = useTranslation()
    const locale = LOCALE_MAP[i18n.language] ?? "en-US"
    const adminUser = useAdminAuthStore(s => s.user)
    const [, navigate] = useLocation()
    const [rulesAgreed, setRulesAgreed] = useState(false)
    const [rulesModalOpen, setRulesModalOpen] = useState(false)

    const { data: areal, isLoading } = useQuery({
        queryKey: ["areal-detail", slug, i18n.language],
        queryFn: () => api.get<ArealPublicDetail>(`/areals/public/${slug}?locale=${i18n.language}`),
        retry: false,
    })

    const { data: adminAreals } = useQuery({
        queryKey: ["admin", "areals"],
        queryFn: () => api.get<ArealResponse[]>("/areals"),
        enabled: !!adminUser,
        staleTime: 10 * 60 * 1000,
    })

    const isCampMember = !!areal && !!adminAreals?.some(a => a.id === areal.id)

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-24">
                <div className="text-slate-400 font-bold">{t("guest.campDetail.loading")}</div>
            </div>
        )
    }

    if (!areal) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-red-700 font-bold">
                {t("guest.campDetail.notFound")}
            </div>
        )
    }

    const coverPhoto = areal.photos.find(p => p.isPrimary) ?? areal.photos[0]
    const galleryPhotos = areal.photos.filter(p => p !== coverPhoto)
    const maintenanceCount = areal.spotCounts.find(s => s.status === "maintenance")?.count ?? 0
    const availableToday = Math.max(0, areal.spotCount - maintenanceCount - areal.bookedToday)
    const priceList = areal.priceLists[0]

    return (
        <div className="max-w-3xl mx-auto pb-6 sm:pb-6">
            {/* Back link */}
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-black text-slate-500 hover:text-slate-800 transition-colors mb-6">
                <ChevronLeft className="size-4" />
                {t("guest.campDetail.back")}
            </Link>

            {/* Hero photo */}
            {coverPhoto && (
                <div className="w-full h-52 sm:h-72 rounded-[40px] overflow-hidden mb-6 shadow-xl">
                    <img
                        src={coverPhoto.url}
                        alt={coverPhoto.altText ?? areal.name}
                        className="w-full h-full object-cover"
                    />
                </div>
            )}

            {/* Title card */}
            <div className="bg-white rounded-[40px] border border-slate-100 shadow-xl p-5 sm:p-8 mb-6">
                <div className="flex items-start justify-between gap-4 mb-3">
                    <h1 className="text-3xl font-black tracking-tight text-slate-900">{areal.name}</h1>
                    {isCampMember && (
                        <Link
                            to="~/admin"
                            className="text-xs font-black uppercase tracking-widest text-slate-400 hover:text-brand transition-colors shrink-0 mt-1"
                        >
                            {t("guest.campDetail.manage")}
                        </Link>
                    )}
                </div>
                <div className="flex items-center gap-1.5 text-slate-500 font-bold text-sm mb-4">
                    <MapPin className="size-4 shrink-0" />
                    <span>{areal.address && `${areal.address}, `}{areal.city}, {areal.country}</span>
                </div>
                {areal.description && (
                    <CollapsibleText>
                        {isHtmlContent(areal.description)
                            ? <div className="text-slate-600 font-bold leading-relaxed rich-text" dangerouslySetInnerHTML={{ __html: sanitizeRichText(areal.description) }} />
                            : <p className="text-slate-600 font-bold leading-relaxed whitespace-pre-line">{areal.description}</p>
                        }
                    </CollapsibleText>
                )}
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <div className="bg-white rounded-[28px] border border-slate-100 shadow-md p-4 text-center">
                    <Tent className="size-5 text-brand mx-auto mb-1" />
                    <p className="text-xl font-black text-slate-900">{availableToday}</p>
                    <p className="text-xs font-black uppercase tracking-widest text-slate-400">{t("guest.campDetail.available")}</p>
                </div>
                <div className="bg-white rounded-[28px] border border-slate-100 shadow-md p-4 text-center">
                    <Users className="size-5 text-slate-400 mx-auto mb-1" />
                    <p className="text-xl font-black text-slate-900">{areal.spotCount}</p>
                    <p className="text-xs font-black uppercase tracking-widest text-slate-400">{t("guest.campDetail.totalSpots")}</p>
                </div>
                {areal.checkInTime && (
                    <div className="bg-white rounded-[28px] border border-slate-100 shadow-md p-4 text-center">
                        <Clock className="size-5 text-slate-400 mx-auto mb-1" />
                        <p className="text-xl font-black text-slate-900">{areal.checkInTime}</p>
                        <p className="text-xs font-black uppercase tracking-widest text-slate-400">{t("guest.campDetail.checkIn")}</p>
                    </div>
                )}
                {areal.checkOutTime && (
                    <div className="bg-white rounded-[28px] border border-slate-100 shadow-md p-4 text-center">
                        <Clock className="size-5 text-slate-400 mx-auto mb-1" />
                        <p className="text-xl font-black text-slate-900">{areal.checkOutTime}</p>
                        <p className="text-xs font-black uppercase tracking-widest text-slate-400">{t("guest.campDetail.checkOut")}</p>
                    </div>
                )}
            </div>

            {/* Pricing */}
            {priceList && (
                <div className="bg-charcoal text-white rounded-[40px] shadow-2xl p-5 sm:p-8 mb-6">
                    <h2 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-4">
                        {t("guest.campDetail.prices")}
                    </h2>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="font-bold text-slate-300">{priceList.name}</span>
                            <span className="text-2xl font-black text-brand">
                                {formatCurrency(priceList.pricePerNight, areal.currency, locale)}
                                <span className="text-sm font-bold text-slate-400 ml-1">{t("guest.campDetail.perNight")}</span>
                            </span>
                        </div>
                        {priceList.localTaxPerPersonPerNight > 0 && (
                            <div className="flex items-center justify-between text-sm">
                                <span className="font-bold text-slate-400">{t("guest.campDetail.localTax")}</span>
                                <span className="font-bold text-slate-300">{formatCurrency(priceList.localTaxPerPersonPerNight, areal.currency, locale)}</span>
                            </div>
                        )}
                        {priceList.petFeePerNight > 0 && (
                            <div className="flex items-center justify-between text-sm">
                                <span className="font-bold text-slate-400">{t("guest.campDetail.petFee")}</span>
                                <span className="font-bold text-slate-300">{formatCurrency(priceList.petFeePerNight, areal.currency, locale)}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Photo gallery */}
            {galleryPhotos.length > 0 && (
                <div className="mb-6">
                    <h2 className="text-xs font-black uppercase tracking-widest text-slate-600 mb-3">
                        {t("guest.campDetail.gallery")}
                    </h2>
                    <PhotoGallery
                        photos={galleryPhotos.map(p => ({ url: p.url, altText: p.altText ?? areal.name }))}
                    />
                </div>
            )}

            {/* Operating rules */}
            {areal.operatingRules && (
                <div className="bg-white rounded-[40px] border border-slate-100 shadow-xl p-5 sm:p-8 mb-6">
                    <h2 className="text-xs font-black uppercase tracking-widest text-slate-600 mb-4">
                        {t("guest.campDetail.operatingRules")}
                    </h2>
                    <div className="relative mb-3">
                        <div className="overflow-hidden" style={{ maxHeight: 140 }}>
                            {isHtmlContent(areal.operatingRules)
                                ? <div className="text-slate-700 font-medium leading-relaxed rich-text" dangerouslySetInnerHTML={{ __html: sanitizeRichText(areal.operatingRules) }} />
                                : <p className="text-slate-700 font-medium whitespace-pre-line leading-relaxed">{areal.operatingRules}</p>
                            }
                        </div>
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent" />
                    </div>
                    <button
                        type="button"
                        onClick={() => setRulesModalOpen(true)}
                        className="mb-5 inline-flex items-center gap-1 text-sm font-black text-brand hover:text-brand-dark transition-colors"
                    >
                        {t("guest.campDetail.readFullRules")}
                    </button>
                    <label className="flex items-start gap-3 p-4 rounded-2xl border border-slate-200 cursor-pointer hover:border-brand transition-colors has-[:checked]:border-brand has-[:checked]:bg-brand/5">
                        <input
                            type="checkbox"
                            checked={rulesAgreed}
                            onChange={(e) => setRulesAgreed(e.target.checked)}
                            className="accent-brand mt-1 size-4 shrink-0"
                        />
                        <span className="font-black text-slate-800 text-sm">{t("guest.campDetail.agreeRules")}</span>
                    </label>
                </div>
            )}

            <OperatingRulesModal
                open={rulesModalOpen}
                onClose={() => setRulesModalOpen(false)}
                title={t("guest.campDetail.operatingRules")}
                content={areal.operatingRules ?? ""}
            />

            {/* Book now CTA */}
            {(() => {
                const mustAgree = !!areal.operatingRules
                const canBook = !mustAgree || rulesAgreed
                const onBookClick = () => { if (canBook) navigate(`/a/${slug}/book`) }
                return (
                    <>
                        {/* Fixed CTA — mobile only */}
                        <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur border-t border-slate-100 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] flex justify-center">
                            <button
                                type="button"
                                onClick={onBookClick}
                                disabled={!canBook}
                                className="w-full max-w-sm bg-brand text-white font-black rounded-[28px] px-10 py-4 shadow-xl hover:bg-brand-dark active:scale-95 transition-all duration-200 text-lg disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100"
                            >
                                {t("guest.campDetail.bookNow")}
                            </button>
                        </div>

                        {/* Inline CTA — desktop */}
                        <div className="hidden sm:block">
                            <button
                                type="button"
                                onClick={onBookClick}
                                disabled={!canBook}
                                className="w-full bg-brand text-white font-black rounded-[28px] px-10 py-4 shadow-xl hover:bg-brand-dark active:scale-95 transition-all duration-200 text-lg disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100"
                            >
                                {t("guest.campDetail.bookNow")}
                            </button>
                        </div>
                    </>
                )
            })()}

            <AIGuidePanel arealId={areal.id} arealName={areal.name} />
        </div>
    )
}
